import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent, MutableRefObject } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  LineStyle,
  LineSeries,
  TickMarkType,
  createChart,
  type CandlestickData,
  type IChartApi,
  type IRange,
  type LineData,
  type Time,
  type UTCTimestamp,
} from 'lightweight-charts';
import { getDepositWithdrawStatus, type DepositWithdrawExchange, type DepositWithdrawRow } from '@/services/depositWithdrawApi';
import { getFundingRates, type FundingRateRecord } from '@/services/fundingRatesApi';
import { getIndexPriceHistory, type IndexPriceExchange } from '@/services/indexPriceApi';
import { getMarkPriceHistory, type MarkPriceExchange } from '@/services/markPriceApi';
import { getOpenInterest, type OpenInterestRecord } from '@/services/openInterestApi';
import { getPremiumHistory, type PremiumExchange } from '@/services/premiumIndexApi';
import { getTradingVolume, type TradingVolumeRecord } from '@/services/tradingVolumeApi';
import {
  getCandles,
  getDetailedSpread,
  getExchanges,
  getIndexAdjustedSpread,
  type DetailedSpreadDataPoint,
} from '@/services/spreadApi';
import { getInstrumentById, type InstrumentInfo } from '@/services/instrumentsApi';
import { formatCandleTooltipValue } from '@/lib/chartFormat';
import {
  buildForwardDetailedSpreadLines,
  buildReverseDetailedSpreadLines,
  detailedSpreadViewModeLabel,
  type DetailedSpreadViewMode,
} from '@/lib/detailedSpreadChartLines';
import { normalizeSymbolInput, symbolToExchangeInstId } from '@/lib/exchangeInstId';
import './Overview.css';

const CHART_HEIGHT = 360;
const DEFAULT_INTERVAL_SECONDS = 300;
const DEFAULT_HISTORY_INTERVAL = '5m';
const DEFAULT_SMA_PERIOD = 2000;
const EXCHANGE1_COLOR = '#2563eb';
const EXCHANGE2_COLOR = '#f97316';

interface OverviewConfig {
  symbol: string;
  exchange1: string;
  exchange2: string;
  startTime: string;
  endTime: string;
  interval: number;
  historyInterval: string;
  smaPeriod: number;
}

interface ChartSyncGroup {
  charts: Set<IChartApi>;
  range: IRange<Time> | null;
  syncing: boolean;
  suppressUntil: number;
}

type ChartSyncGroupRef = MutableRefObject<ChartSyncGroup>;

interface CandleLine {
  key: string;
  label: string;
  color: string;
  data: CandlestickData<UTCTimestamp>[];
}

interface ValueLine {
  key: string;
  label: string;
  color: string;
  data: LineData<UTCTimestamp>[];
  lineStyle?: LineStyle;
  lineWidth?: 1 | 2 | 3 | 4;
}

interface DepositResult {
  exchange: string;
  label: string;
  rows: DepositWithdrawRow[];
  error: string | null;
}

interface FundingSettlementTimes {
  left: number | null;
  right: number | null;
}

interface OverviewErrors {
  price?: string;
  indexAdjusted?: string;
  forwardDetailed?: string;
  reverseDetailed?: string;
  funding?: string;
  premium?: string;
  mark?: string;
  index?: string;
  openInterest?: string;
  tradingVolume?: string;
  deposit?: string;
}

type OverviewRefreshKey =
  | 'price'
  | 'forwardDetailed'
  | 'reverseDetailed'
  | 'indexAdjusted'
  | 'funding'
  | 'openInterest'
  | 'tradingVolume'
  | 'premium'
  | 'mark'
  | 'index';

const createChartSyncGroup = (): ChartSyncGroup => ({
  charts: new Set<IChartApi>(),
  range: null,
  syncing: false,
  suppressUntil: 0,
});

const toDateTimeInput = (date: Date): string => date.toISOString().slice(0, 16);

const inputUtcToIso = (value: string): string => {
  if (!value) return '';
  return new Date(`${value}:00Z`).toISOString();
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

const timeToUtcDate = (time: Time): Date => {
  if (typeof time === 'number') return new Date(time * 1000);
  if (typeof time === 'string') return new Date(`${time}T00:00:00Z`);
  return new Date(Date.UTC(time.year, time.month - 1, time.day));
};

const formatUtcDateTime = (time: Time): string => {
  const date = timeToUtcDate(time);
  return [
    date.getUTCFullYear(),
    '-',
    pad2(date.getUTCMonth() + 1),
    '-',
    pad2(date.getUTCDate()),
    ' ',
    pad2(date.getUTCHours()),
    ':',
    pad2(date.getUTCMinutes()),
  ].join('');
};

const formatUtcTick = (time: Time, tickMarkType: TickMarkType): string => {
  const date = timeToUtcDate(time);
  if (tickMarkType === TickMarkType.Year) return String(date.getUTCFullYear());
  if (tickMarkType === TickMarkType.Month) return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}`;
  if (tickMarkType === TickMarkType.DayOfMonth) return `${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
  return `${pad2(date.getUTCHours())}:${pad2(date.getUTCMinutes())}`;
};

const toUtcTimestamp = (timestamp: string): UTCTimestamp | null => {
  const value = new Date(timestamp).getTime();
  if (!Number.isFinite(value)) return null;
  return Math.floor(value / 1000) as UTCTimestamp;
};

const sortByTime = <T extends { time: UTCTimestamp }>(data: T[]): T[] =>
  [...data].sort((a, b) => Number(a.time) - Number(b.time));

const exchangeToSlug = (exchange: string): string => exchange.trim().toLowerCase().replace(/\./g, '');

const toOverviewExchange = (value: string): string => {
  const normalized = value.trim().toLowerCase();
  if (normalized === 'okx') return 'OKX';
  if (normalized === 'bybit') return 'Bybit';
  if (normalized === 'binance') return 'Binance';
  if (normalized === 'bitget') return 'Bitget';
  if (normalized === 'gateio') return 'Gateio';
  if (normalized === 'hyperliquid') return 'Hyperliquid';
  if (normalized === 'lighter') return 'Lighter';
  if (normalized === 'paradex') return 'Paradex';
  return value;
};

const isPremiumExchange = (value: string): value is PremiumExchange =>
  ['bybit', 'okx', 'binance', 'gateio'].includes(value);

const isMarkExchange = (value: string): value is MarkPriceExchange =>
  ['bybit', 'okx', 'binance', 'gateio', 'bitget'].includes(value);

const isIndexExchange = (value: string): value is IndexPriceExchange =>
  ['bybit', 'okx', 'binance', 'gateio', 'bitget'].includes(value);

const isOpenInterestExchange = (value: string): boolean => ['okx', 'binance', 'bybit'].includes(value);

const isTradingVolumeExchange = (value: string): boolean =>
  ['binance', 'okx', 'bybit', 'gateio', 'bitget', 'hyperliquid'].includes(value);

const OVERVIEW_DEPOSIT_EXCHANGES: Array<{ value: DepositWithdrawExchange; label: string }> = [
  { value: 'binance', label: 'Binance' },
  { value: 'bybit', label: 'Bybit' },
  { value: 'okx', label: 'OKX' },
  { value: 'gateio', label: 'Gateio' },
  { value: 'bitget', label: 'Bitget' },
];

const formatPrice = (value: number): string => {
  if (!Number.isFinite(value)) return '-';
  if (Math.abs(value) >= 1000) return value.toFixed(2);
  if (Math.abs(value) >= 1) return value.toFixed(4);
  return value.toPrecision(6);
};

const createTooltip = (container: HTMLDivElement): HTMLDivElement => {
  const tooltip = document.createElement('div');
  tooltip.className = 'overview-tooltip';
  tooltip.style.display = 'none';
  container.appendChild(tooltip);
  return tooltip;
};

const renderTooltip = (
  tooltip: HTMLDivElement,
  title: string,
  rows: Array<{ label: string; value: string; color?: string }>,
) => {
  tooltip.replaceChildren();

  const titleEl = document.createElement('div');
  titleEl.className = 'overview-tooltip-title';
  titleEl.textContent = title;
  tooltip.appendChild(titleEl);

  rows.forEach((row) => {
    const rowEl = document.createElement('div');
    rowEl.className = 'overview-tooltip-row';

    const labelEl = document.createElement('span');
    labelEl.className = 'overview-tooltip-label';
    if (row.color) {
      const dot = document.createElement('i');
      dot.className = 'overview-dot';
      dot.style.background = row.color;
      labelEl.appendChild(dot);
    }
    labelEl.append(document.createTextNode(row.label));

    const valueEl = document.createElement('span');
    valueEl.className = 'overview-tooltip-value';
    valueEl.textContent = row.value;

    rowEl.append(labelEl, valueEl);
    tooltip.appendChild(rowEl);
  });
};

const positionTooltip = (tooltip: HTMLDivElement, container: HTMLDivElement, point: { x: number; y: number }) => {
  const padding = 12;
  tooltip.style.display = 'block';
  const left =
    point.x + padding + tooltip.offsetWidth > container.clientWidth
      ? Math.max(padding, point.x - tooltip.offsetWidth - padding)
      : point.x + padding;
  const top =
    point.y + padding + tooltip.offsetHeight > container.clientHeight
      ? Math.max(padding, point.y - tooltip.offsetHeight - padding)
      : point.y + padding;
  tooltip.style.left = `${left}px`;
  tooltip.style.top = `${top}px`;
};

const bindChartTimeSync = (chart: IChartApi, syncGroupRef: ChartSyncGroupRef) => {
  const group = syncGroupRef.current;
  group.charts.add(chart);

  const currentRange = chart.timeScale().getVisibleRange();
  if (group.range) {
    group.syncing = true;
    group.suppressUntil = performance.now() + 200;
    chart.timeScale().setVisibleRange(group.range);
    window.setTimeout(() => {
      group.syncing = false;
    }, 0);
  } else if (currentRange) {
    group.range = currentRange;
  }

  const handleRangeChange = (range: IRange<Time> | null) => {
    if (!range || group.syncing || performance.now() < group.suppressUntil) return;
    group.range = range;
    group.syncing = true;
    group.suppressUntil = performance.now() + 200;
    group.charts.forEach((otherChart) => {
      if (otherChart === chart) return;
      try {
        otherChart.timeScale().setVisibleRange(range);
      } catch {}
    });
    window.setTimeout(() => {
      group.syncing = false;
    }, 0);
  };

  chart.timeScale().subscribeVisibleTimeRangeChange(handleRangeChange);

  return () => {
    chart.timeScale().unsubscribeVisibleTimeRangeChange(handleRangeChange);
    group.charts.delete(chart);
  };
};

const getSeriesData = <T,>(seriesData: Map<unknown, unknown>, series: unknown): T | undefined =>
  seriesData.get(series) as T | undefined;

function TradingViewPairCandleChart({ series, syncGroupRef }: { series: CandleLine[]; syncGroupRef: ChartSyncGroupRef }) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';

    const chart = createChart(container, {
      width: container.clientWidth || 900,
      height: CHART_HEIGHT,
      layout: { background: { type: ColorType.Solid, color: '#ffffff' }, textColor: '#4b5563' },
      grid: { vertLines: { color: '#eef2f7' }, horzLines: { color: '#eef2f7' } },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: formatUtcTick,
      },
    });

    const tooltipSeries: Array<{ series: unknown; label: string; color: string }> = [];
    series.forEach((line) => {
      if (line.data.length === 0) return;
      const api = chart.addSeries(CandlestickSeries, {
        upColor: line.color,
        downColor: line.color,
        borderColor: line.color,
        wickColor: line.color,
        priceFormat: { type: 'price', precision: 6, minMove: 0.000001 },
      });
      api.setData(line.data);
      tooltipSeries.push({ series: api, label: line.label, color: line.color });
    });

    const tooltip = createTooltip(container);
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipSeries
        .map((item) => {
          const point = getSeriesData<CandlestickData<Time>>(param.seriesData as Map<unknown, unknown>, item.series);
          if (!point || !('open' in point)) return null;
          return {
            label: item.label,
            value: formatCandleTooltipValue(point),
            color: item.color,
          };
        })
        .filter((row): row is { label: string; value: string; color: string } => row != null);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      positionTooltip(tooltip, container, param.point);
    });

    chart.timeScale().fitContent();
    const unbindSync = bindChartTimeSync(chart, syncGroupRef);
    const resizeObserver = new ResizeObserver(() => chart.applyOptions({ width: container.clientWidth || 900 }));
    resizeObserver.observe(container);

    return () => {
      unbindSync();
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [series, syncGroupRef]);

  return <div className="overview-chart" ref={containerRef} />;
}

function TradingViewLineChart({
  lines,
  syncGroupRef,
  valueSuffix = '%',
  valuePrecision = 6,
}: {
  lines: ValueLine[];
  syncGroupRef: ChartSyncGroupRef;
  valueSuffix?: string;
  valuePrecision?: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = '';
    const minMove = 10 ** -valuePrecision;

    const chart = createChart(container, {
      width: container.clientWidth || 900,
      height: CHART_HEIGHT,
      layout: { background: { type: ColorType.Solid, color: '#ffffff' }, textColor: '#4b5563' },
      grid: { vertLines: { color: '#eef2f7' }, horzLines: { color: '#eef2f7' } },
      crosshair: { mode: CrosshairMode.Normal },
      localization: { timeFormatter: formatUtcDateTime },
      rightPriceScale: { borderColor: '#e5e7eb' },
      timeScale: {
        borderColor: '#e5e7eb',
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: formatUtcTick,
      },
    });

    const tooltipLines: Array<{ series: unknown; label: string; color: string }> = [];
    lines.forEach((line) => {
      if (line.data.length === 0) return;
      const api = chart.addSeries(LineSeries, {
        color: line.color,
        lineStyle: line.lineStyle ?? LineStyle.Solid,
        lineWidth: line.lineWidth ?? 2,
        priceFormat: { type: 'price', precision: valuePrecision, minMove },
      });
      api.setData(line.data);
      tooltipLines.push({ series: api, label: line.label, color: line.color });
    });

    const tooltip = createTooltip(container);
    chart.subscribeCrosshairMove((param) => {
      if (!param.point || !param.time) {
        tooltip.style.display = 'none';
        return;
      }
      const rows = tooltipLines
        .map((item) => {
          const point = getSeriesData<LineData<Time>>(param.seriesData as Map<unknown, unknown>, item.series);
          if (!point || !('value' in point)) return null;
          return { label: item.label, value: `${point.value.toFixed(valuePrecision)}${valueSuffix}`, color: item.color };
        })
        .filter((row): row is { label: string; value: string; color: string } => row != null);
      if (rows.length === 0) {
        tooltip.style.display = 'none';
        return;
      }
      renderTooltip(tooltip, formatUtcDateTime(param.time), rows);
      positionTooltip(tooltip, container, param.point);
    });

    chart.timeScale().fitContent();
    const unbindSync = bindChartTimeSync(chart, syncGroupRef);
    const resizeObserver = new ResizeObserver(() => chart.applyOptions({ width: container.clientWidth || 900 }));
    resizeObserver.observe(container);

    return () => {
      unbindSync();
      resizeObserver.disconnect();
      chart.remove();
    };
  }, [lines, syncGroupRef, valuePrecision, valueSuffix]);

  return <div className="overview-chart" ref={containerRef} />;
}

const mapServerCandle = (data: Array<{ timestamp: string; open: number; high: number; low: number; close: number }>) =>
  sortByTime(
    data
      .map((point): CandlestickData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.timestamp);
        if (time == null) return null;
        return { time, open: point.open, high: point.high, low: point.low, close: point.close };
      })
      .filter((point): point is CandlestickData<UTCTimestamp> => point != null),
  );

const mapMsCandle = (data: Array<{ ts_ms: number; open: number; high: number; low: number; close: number }>) =>
  sortByTime(
    data
      .map((point): CandlestickData<UTCTimestamp> | null => {
        if (!Number.isFinite(point.ts_ms)) return null;
        return {
          time: Math.floor(point.ts_ms / 1000) as UTCTimestamp,
          open: point.open,
          high: point.high,
          low: point.low,
          close: point.close,
        };
      })
      .filter((point): point is CandlestickData<UTCTimestamp> => point != null),
  );

const mapFundingLine = (data: FundingRateRecord[]) =>
  sortByTime(
    data
      .map((point): LineData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.ts);
        if (time == null || !Number.isFinite(point.rate)) return null;
        return { time, value: point.rate * 100 };
      })
      .filter((point): point is LineData<UTCTimestamp> => point != null),
  );

const mapOpenInterestUsdLine = (data: OpenInterestRecord[]) =>
  sortByTime(
    data
      .map((point): LineData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.ts);
        if (time == null || !Number.isFinite(point.open_interest_usd)) return null;
        return { time, value: point.open_interest_usd };
      })
      .filter((point): point is LineData<UTCTimestamp> => point != null),
  );

const mapTradingVolumeQuoteLine = (data: TradingVolumeRecord[]) =>
  sortByTime(
    data
      .map((point): LineData<UTCTimestamp> | null => {
        const time = toUtcTimestamp(point.ts);
        if (time == null || !Number.isFinite(point.volume_quote_24h)) return null;
        return { time, value: point.volume_quote_24h };
      })
      .filter((point): point is LineData<UTCTimestamp> => point != null),
  );

const normalizeFundingTimeMs = (fundingTime: number): number | null => {
  if (!Number.isFinite(fundingTime) || fundingTime <= 0) return null;
  // Most sources return seconds; some may return milliseconds.
  return fundingTime >= 1_000_000_000_000 ? fundingTime : fundingTime * 1000;
};

const formatUtcDateTimeByMs = (timestampMs: number): string => {
  const date = new Date(timestampMs);
  if (!Number.isFinite(date.getTime())) return '-';
  return [
    date.getUTCFullYear(),
    '-',
    pad2(date.getUTCMonth() + 1),
    '-',
    pad2(date.getUTCDate()),
    ' ',
    pad2(date.getUTCHours()),
    ':',
    pad2(date.getUTCMinutes()),
    ':',
    pad2(date.getUTCSeconds()),
    ' UTC',
  ].join('');
};

const formatDuration = (durationMs: number): string => {
  if (!Number.isFinite(durationMs)) return '-';
  if (durationMs <= 0) return '已结算';
  const totalSeconds = Math.floor(durationMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${pad2(hours)}h ${pad2(minutes)}m ${pad2(seconds)}s`;
  return `${pad2(hours)}h ${pad2(minutes)}m ${pad2(seconds)}s`;
};

const formatFundingSettlement = (label: string, timestampMs: number | null, nowMs: number): string => {
  if (timestampMs == null) return `${label}结算: -`;
  return `${label}结算: ${formatUtcDateTimeByMs(timestampMs)} 剩余 ${formatDuration(timestampMs - nowMs)}`;
};

function ChartCard({
  title,
  subtitle,
  error,
  onRefresh,
  isRefreshing = false,
  refreshDisabled = false,
  children,
}: {
  title: string;
  subtitle: string;
  error?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  refreshDisabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <section className="overview-card">
      <div className="overview-card-title">
        <div>
          <h2>{title}</h2>
          <span>{subtitle}</span>
        </div>
        {onRefresh && (
          <button
            type="button"
            className="overview-card-refresh"
            onClick={onRefresh}
            disabled={isRefreshing || refreshDisabled}
          >
            {isRefreshing ? '刷新中...' : '刷新'}
          </button>
        )}
      </div>
      {error ? <div className="overview-card-error">{error}</div> : children}
    </section>
  );
}

export interface OverviewProps {
  embedded?: boolean;
}

export function Overview({ embedded = false }: OverviewProps) {
  const [searchParams] = useSearchParams();
  const defaultEnd = useMemo(() => new Date(), []);
  const defaultStart = useMemo(() => new Date(defaultEnd.getTime() - 7 * 24 * 60 * 60 * 1000), [defaultEnd]);
  const [config, setConfig] = useState<OverviewConfig>({
    symbol: 'BTC',
    exchange1: 'OKX',
    exchange2: 'Bybit',
    startTime: toDateTimeInput(defaultStart),
    endTime: toDateTimeInput(defaultEnd),
    interval: DEFAULT_INTERVAL_SECONDS,
    historyInterval: DEFAULT_HISTORY_INTERVAL,
    smaPeriod: DEFAULT_SMA_PERIOD,
  });
  const [exchanges, setExchanges] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [refreshingCharts, setRefreshingCharts] = useState<Partial<Record<OverviewRefreshKey, boolean>>>({});
  const [pageError, setPageError] = useState<string | null>(null);
  const [errors, setErrors] = useState<OverviewErrors>({});
  const [priceCandles, setPriceCandles] = useState<CandleLine[]>([]);
  const [markCandles, setMarkCandles] = useState<CandleLine[]>([]);
  const [indexCandles, setIndexCandles] = useState<CandleLine[]>([]);
  const [indexAdjustedLines, setIndexAdjustedLines] = useState<ValueLine[]>([]);
  const [detailedSpreadRows, setDetailedSpreadRows] = useState<DetailedSpreadDataPoint[]>([]);
  const [forwardSpreadViewMode, setForwardSpreadViewMode] = useState<DetailedSpreadViewMode>('orderbook');
  const [reverseSpreadViewMode, setReverseSpreadViewMode] = useState<DetailedSpreadViewMode>('orderbook');
  const [premiumLines, setPremiumLines] = useState<ValueLine[]>([]);
  const [fundingLines, setFundingLines] = useState<ValueLine[]>([]);
  const [openInterestLines, setOpenInterestLines] = useState<ValueLine[]>([]);
  const [tradingVolumeLines, setTradingVolumeLines] = useState<ValueLine[]>([]);
  const [fundingSettlementTimes, setFundingSettlementTimes] = useState<FundingSettlementTimes>({ left: null, right: null });
  const [nowMs, setNowMs] = useState(() => Date.now());
  const [depositResults, setDepositResults] = useState<DepositResult[]>([]);
  const [instrumentInfo1, setInstrumentInfo1] = useState<InstrumentInfo | null>(null);
  const [instrumentInfo2, setInstrumentInfo2] = useState<InstrumentInfo | null>(null);
  const [instrumentInfoLoading, setInstrumentInfoLoading] = useState(false);
  const [instrumentInfoError, setInstrumentInfoError] = useState<string | null>(null);
  const [pendingAutoQuery, setPendingAutoQuery] = useState(false);
  const chartSyncGroupRef = useRef<ChartSyncGroup>(createChartSyncGroup());
  const handledSearchRef = useRef<string>('');
  const querySeqRef = useRef(0);

  const symbol = normalizeSymbolInput(config.symbol);
  const instId1 = symbolToExchangeInstId(config.exchange1, symbol);
  const instId2 = symbolToExchangeInstId(config.exchange2, symbol);
  const exchange1Slug = exchangeToSlug(config.exchange1);
  const exchange2Slug = exchangeToSlug(config.exchange2);
  const fundingSubtitle = useMemo(() => {
    const pair = `${config.exchange1}:${instId1} / ${config.exchange2}:${instId2}`;
    const leftSettlement = formatFundingSettlement(config.exchange1, fundingSettlementTimes.left, nowMs);
    const rightSettlement = formatFundingSettlement(config.exchange2, fundingSettlementTimes.right, nowMs);
    return `${pair} | ${leftSettlement} / ${rightSettlement}`;
  }, [config.exchange1, config.exchange2, fundingSettlementTimes.left, fundingSettlementTimes.right, instId1, instId2, nowMs]);

  const fundingChartLines = useMemo(() => {
    const times = fundingLines
      .flatMap((line) => line.data.map((point) => Number(point.time)))
      .filter((time) => Number.isFinite(time))
      .sort((a, b) => a - b);
    if (times.length === 0) return fundingLines;

    const from = times[0] as UTCTimestamp;
    const to = times[times.length - 1] as UTCTimestamp;
    const capLines: ValueLine[] = [];

    const addCapLines = (exchange: string, info: InstrumentInfo | null, color: string) => {
      if (info == null) return;
      [
        { key: 'upper', label: 'upper funding cap', value: info.upper_funding_rate },
        { key: 'lower', label: 'lower funding cap', value: info.lower_funding_rate },
      ].forEach((cap) => {
        if (!Number.isFinite(cap.value)) return;
        capLines.push({
          key: `${exchange}-${cap.key}-funding-cap`,
          label: `${exchange} ${cap.label}`,
          color,
          lineStyle: LineStyle.Dashed,
          lineWidth: 1,
          data: [
            { time: from, value: cap.value * 100 },
            { time: to, value: cap.value * 100 },
          ],
        });
      });
    };

    addCapLines(config.exchange1, instrumentInfo1, EXCHANGE1_COLOR);
    addCapLines(config.exchange2, instrumentInfo2, EXCHANGE2_COLOR);
    return [...fundingLines, ...capLines];
  }, [config.exchange1, config.exchange2, fundingLines, instrumentInfo1, instrumentInfo2]);

  const forwardDetailedLines = useMemo(
    () =>
      buildForwardDetailedSpreadLines(detailedSpreadRows, forwardSpreadViewMode, {
        primary: EXCHANGE1_COLOR,
        secondary: EXCHANGE2_COLOR,
      }),
    [detailedSpreadRows, forwardSpreadViewMode],
  );

  const reverseDetailedLines = useMemo(
    () =>
      buildReverseDetailedSpreadLines(detailedSpreadRows, reverseSpreadViewMode, {
        primary: EXCHANGE1_COLOR,
        secondary: EXCHANGE2_COLOR,
      }),
    [detailedSpreadRows, reverseSpreadViewMode],
  );

  useEffect(() => {
    getExchanges()
      .then((res) => setExchanges(res.exchanges ?? []))
      .catch(() => setExchanges(['OKX', 'Bybit', 'Binance', 'Bitget', 'Gateio', 'Hyperliquid']));
  }, []);

  useEffect(() => {
    if (fundingSettlementTimes.left == null && fundingSettlementTimes.right == null) return;
    const timer = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [fundingSettlementTimes.left, fundingSettlementTimes.right]);

  useEffect(() => {
    const queryKey = searchParams.toString();
    if (!queryKey || handledSearchRef.current === queryKey) return;

    const symbolParam = searchParams.get('symbol')?.trim() ?? '';
    const exchange1Param = searchParams.get('exchange1')?.trim() ?? '';
    const exchange2Param = searchParams.get('exchange2')?.trim() ?? '';
    const autoQueryParam = searchParams.get('autoQuery')?.trim().toLowerCase();

    const hasPrefill = Boolean(symbolParam || exchange1Param || exchange2Param);
    handledSearchRef.current = queryKey;
    if (!hasPrefill) return;

    setConfig((prev) => ({
      ...prev,
      symbol: symbolParam || prev.symbol,
      exchange1: exchange1Param ? toOverviewExchange(exchange1Param) : prev.exchange1,
      exchange2: exchange2Param ? toOverviewExchange(exchange2Param) : prev.exchange2,
    }));

    if (autoQueryParam === '1' || autoQueryParam === 'true' || autoQueryParam === 'yes') {
      setPendingAutoQuery(true);
    }
  }, [searchParams]);

  const setField = (field: keyof OverviewConfig) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;
    setConfig((prev) => ({
      ...prev,
      [field]: event.target instanceof HTMLInputElement && event.target.type === 'number' ? Number(value) : value,
    }));
  };

  const buildQueryContext = useCallback(() => {
    const startTime = inputUtcToIso(config.startTime);
    const endTime = inputUtcToIso(config.endTime);
    const startMs = new Date(startTime).getTime();
    const endMs = new Date(endTime).getTime();
    if (!symbol || !instId1 || !instId2) {
      setPageError('请输入 symbol');
      return null;
    }
    if (startMs >= endMs) {
      setPageError('开始时间必须早于结束时间');
      return null;
    }
    return { startTime, endTime, startMs, endMs };
  }, [config.endTime, config.startTime, instId1, instId2, symbol]);

  const buildLatestEndQueryContext = useCallback(() => {
    const latestEnd = new Date();
    const latestEndInput = toDateTimeInput(latestEnd);
    const startTime = inputUtcToIso(config.startTime);
    const endTime = latestEnd.toISOString();
    const startMs = new Date(startTime).getTime();
    const endMs = latestEnd.getTime();
    setConfig((prev) => ({ ...prev, endTime: latestEndInput }));
    if (!symbol || !instId1 || !instId2) {
      setPageError('请输入 symbol');
      return null;
    }
    if (startMs >= endMs) {
      setPageError('开始时间必须早于最新时间');
      return null;
    }
    return { startTime, endTime, startMs, endMs };
  }, [config.startTime, instId1, instId2, symbol]);

  const runQuery = useCallback(
    (event?: FormEvent) => {
      event?.preventDefault();
      setPageError(null);
      setPriceCandles([]);
      setMarkCandles([]);
      setIndexCandles([]);
      setIndexAdjustedLines([]);
      setDetailedSpreadRows([]);
      setPremiumLines([]);
      setFundingLines([]);
      setOpenInterestLines([]);
      setTradingVolumeLines([]);
      const ctx = buildQueryContext();
      if (!ctx) return;

      const querySeq = querySeqRef.current + 1;
      querySeqRef.current = querySeq;
      let pendingTasks = 11;
      const finishTask = () => {
        pendingTasks -= 1;
        if (pendingTasks === 0 && querySeqRef.current === querySeq) {
          setIsLoading(false);
        }
      };

      setIsLoading(true);
      setErrors({});
      setFundingSettlementTimes({ left: null, right: null });
      setInstrumentInfoLoading(true);
      setInstrumentInfoError(null);
      setInstrumentInfo1(null);
      setInstrumentInfo2(null);
      chartSyncGroupRef.current.range = null;
      chartSyncGroupRef.current.syncing = false;
      chartSyncGroupRef.current.suppressUntil = 0;

      Promise.all([
        getCandles({
          exchange: config.exchange1,
          inst_id: instId1,
          start_time: ctx.startTime,
          end_time: ctx.endTime,
          interval: config.interval,
        }),
        getCandles({
          exchange: config.exchange2,
          inst_id: instId2,
          start_time: ctx.startTime,
          end_time: ctx.endTime,
          interval: config.interval,
        }),
      ])
        .then((value) => {
          if (querySeqRef.current !== querySeq) return;
          setPriceCandles([
            { key: 'price1', label: `${config.exchange1}:${instId1}`, color: EXCHANGE1_COLOR, data: mapServerCandle(value[0].data) },
            { key: 'price2', label: `${config.exchange2}:${instId2}`, color: EXCHANGE2_COLOR, data: mapServerCandle(value[1].data) },
          ]);
          setErrors((prev) => ({ ...prev, price: undefined }));
        })
        .catch((reason: unknown) => {
          if (querySeqRef.current !== querySeq) return;
          setPriceCandles([]);
          setErrors((prev) => ({ ...prev, price: reason instanceof Error ? reason.message : String(reason) }));
        })
        .finally(finishTask);

      getDetailedSpread({
        exchange1: config.exchange1,
        inst_id1: instId1,
        exchange2: config.exchange2,
        inst_id2: instId2,
        start_time: ctx.startTime,
        end_time: ctx.endTime,
        interval: config.interval,
        sma_period: config.smaPeriod,
      })
        .then((value) => {
          if (querySeqRef.current !== querySeq) return;
          setDetailedSpreadRows(value.data);
          setErrors((prev) => ({ ...prev, forwardDetailed: undefined, reverseDetailed: undefined }));
        })
        .catch((reason: unknown) => {
          if (querySeqRef.current !== querySeq) return;
          const msg = reason instanceof Error ? reason.message : String(reason);
          setDetailedSpreadRows([]);
          setErrors((prev) => ({ ...prev, forwardDetailed: msg, reverseDetailed: msg }));
        })
        .finally(finishTask);

      Promise.all([getInstrumentById(instId1, config.exchange1), getInstrumentById(instId2, config.exchange2)])
        .then(([left, right]) => {
          if (querySeqRef.current !== querySeq) return;
          setInstrumentInfo1(left);
          setInstrumentInfo2(right);
          setInstrumentInfoError(null);
        })
        .catch((reason: unknown) => {
          if (querySeqRef.current !== querySeq) return;
          setInstrumentInfo1(null);
          setInstrumentInfo2(null);
          setInstrumentInfoError(reason instanceof Error ? reason.message : String(reason));
        })
        .finally(() => {
          if (querySeqRef.current === querySeq) {
            setInstrumentInfoLoading(false);
          }
          finishTask();
        });

    (config.interval === 60 || config.interval === 300
      ? getIndexAdjustedSpread({
          exchange1: config.exchange1,
          inst_id1: instId1,
          exchange2: config.exchange2,
          inst_id2: instId2,
          start_time: ctx.startTime,
          end_time: ctx.endTime,
          interval: config.interval as 60 | 300,
          symbol1: symbol,
          symbol2: symbol,
          sma_period: config.smaPeriod,
        })
      : Promise.resolve(null)
    )
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        if (value == null) {
          setIndexAdjustedLines([]);
          setErrors((prev) => ({ ...prev, indexAdjusted: 'Index修正Spread仅支持 interval=60(1m) 或 300(5m)。' }));
          return;
        }
        setIndexAdjustedLines([
          {
            key: 'adjusted_spread',
            label: 'adjusted_spread',
            color: EXCHANGE1_COLOR,
            data: sortByTime(
              value.data
                .map((point): LineData<UTCTimestamp> | null => {
                  const time = toUtcTimestamp(point.timestamp);
                  if (time == null || !Number.isFinite(point.adjusted_spread)) return null;
                  return { time, value: point.adjusted_spread };
                })
                .filter((point): point is LineData<UTCTimestamp> => point != null),
            ),
          },
          {
            key: 'orderbook_spread',
            label: 'orderbook_spread',
            color: EXCHANGE2_COLOR,
            data: sortByTime(
              value.data
                .map((point): LineData<UTCTimestamp> | null => {
                  const time = toUtcTimestamp(point.timestamp);
                  if (time == null || !Number.isFinite(point.orderbook_spread)) return null;
                  return { time, value: point.orderbook_spread };
                })
                .filter((point): point is LineData<UTCTimestamp> => point != null),
            ),
          },
          {
            key: 'index_spread',
            label: 'index_spread',
            color: '#7c3aed',
            data: sortByTime(
              value.data
                .map((point): LineData<UTCTimestamp> | null => {
                  const time = toUtcTimestamp(point.timestamp);
                  if (time == null || !Number.isFinite(point.index_spread)) return null;
                  return { time, value: point.index_spread };
                })
                .filter((point): point is LineData<UTCTimestamp> => point != null),
            ),
          },
        ]);
        setErrors((prev) => ({ ...prev, indexAdjusted: undefined }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setIndexAdjustedLines([]);
        setErrors((prev) => ({ ...prev, indexAdjusted: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    Promise.all([
      getFundingRates({
        exchange: config.exchange1,
        inst_id: instId1,
        start_time: ctx.startTime,
        end_time: ctx.endTime,
        interval: config.interval,
      }),
      getFundingRates({
        exchange: config.exchange2,
        inst_id: instId2,
        start_time: ctx.startTime,
        end_time: ctx.endTime,
        interval: config.interval,
      }),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        setFundingSettlementTimes({
          left: normalizeFundingTimeMs(value[0].data[value[0].data.length - 1]?.funding_time),
          right: normalizeFundingTimeMs(value[1].data[value[1].data.length - 1]?.funding_time),
        });
        setFundingLines([
          {
            key: 'funding1',
            label: `${config.exchange1}:${instId1}`,
            color: EXCHANGE1_COLOR,
            data: mapFundingLine(value[0].data),
          },
          {
            key: 'funding2',
            label: `${config.exchange2}:${instId2}`,
            color: EXCHANGE2_COLOR,
            data: mapFundingLine(value[1].data),
          },
        ]);
        setErrors((prev) => ({ ...prev, funding: undefined }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setFundingLines([]);
        setFundingSettlementTimes({ left: null, right: null });
        setErrors((prev) => ({ ...prev, funding: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    Promise.allSettled([
      isOpenInterestExchange(exchange1Slug)
        ? getOpenInterest({
            exchange: config.exchange1,
            inst_id: instId1,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            limit: 10000,
          })
        : Promise.resolve(null),
      isOpenInterestExchange(exchange2Slug)
        ? getOpenInterest({
            exchange: config.exchange2,
            inst_id: instId2,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            limit: 10000,
          })
        : Promise.resolve(null),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const [openInterest1, openInterest2] = value;
        const oi1 = openInterest1.status === 'fulfilled' ? openInterest1.value : null;
        const oi2 = openInterest2.status === 'fulfilled' ? openInterest2.value : null;
        setOpenInterestLines([
          {
            key: 'open_interest1',
            label: `${config.exchange1}:${instId1} OI USD`,
            color: EXCHANGE1_COLOR,
            data: oi1 ? mapOpenInterestUsdLine(oi1.data) : [],
          },
          {
            key: 'open_interest2',
            label: `${config.exchange2}:${instId2} OI USD`,
            color: EXCHANGE2_COLOR,
            data: oi2 ? mapOpenInterestUsdLine(oi2.data) : [],
          },
        ]);
        const unsupportedLabels = [
          !isOpenInterestExchange(exchange1Slug) ? config.exchange1 : null,
          !isOpenInterestExchange(exchange2Slug) ? config.exchange2 : null,
        ].filter((value): value is string => value != null);
        const failedLabels = [
          openInterest1.status === 'rejected' ? config.exchange1 : null,
          openInterest2.status === 'rejected' ? config.exchange2 : null,
        ].filter((value): value is string => value != null);
        const emptyLabels = [
          oi1 != null && oi1.data.length === 0 ? config.exchange1 : null,
          oi2 != null && oi2.data.length === 0 ? config.exchange2 : null,
        ].filter((value): value is string => value != null);
        const issueLabels = [...unsupportedLabels, ...failedLabels, ...emptyLabels];
        setErrors((prev) => ({
          ...prev,
          openInterest:
            issueLabels.length > 0
              ? `Open Interest 部分查询无数据或不可用：${issueLabels.join(' / ')}`
              : undefined,
        }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setOpenInterestLines([]);
        setErrors((prev) => ({ ...prev, openInterest: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    Promise.allSettled([
      isTradingVolumeExchange(exchange1Slug)
        ? getTradingVolume({
            exchange: config.exchange1,
            market_type: 'future',
            symbol,
            inst_id: instId1,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            limit: 50000,
          })
        : Promise.resolve(null),
      isTradingVolumeExchange(exchange2Slug)
        ? getTradingVolume({
            exchange: config.exchange2,
            market_type: 'future',
            symbol,
            inst_id: instId2,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            limit: 50000,
          })
        : Promise.resolve(null),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const [volume1, volume2] = value;
        const v1 = volume1.status === 'fulfilled' ? volume1.value : null;
        const v2 = volume2.status === 'fulfilled' ? volume2.value : null;
        setTradingVolumeLines([
          {
            key: 'trading_volume1',
            label: `${config.exchange1}:${instId1} 24h quote volume`,
            color: EXCHANGE1_COLOR,
            data: v1 ? mapTradingVolumeQuoteLine(v1.data) : [],
          },
          {
            key: 'trading_volume2',
            label: `${config.exchange2}:${instId2} 24h quote volume`,
            color: EXCHANGE2_COLOR,
            data: v2 ? mapTradingVolumeQuoteLine(v2.data) : [],
          },
        ]);
        const unsupportedLabels = [
          !isTradingVolumeExchange(exchange1Slug) ? config.exchange1 : null,
          !isTradingVolumeExchange(exchange2Slug) ? config.exchange2 : null,
        ].filter((value): value is string => value != null);
        const failedLabels = [
          volume1.status === 'rejected' ? config.exchange1 : null,
          volume2.status === 'rejected' ? config.exchange2 : null,
        ].filter((value): value is string => value != null);
        setErrors((prev) => ({
          ...prev,
          tradingVolume:
            unsupportedLabels.length >= 2 || failedLabels.length >= 2
              ? `Trading Volume 查询不可用：${[...unsupportedLabels, ...failedLabels].join(' / ')}`
              : undefined,
        }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setTradingVolumeLines([]);
        setErrors((prev) => ({ ...prev, tradingVolume: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    Promise.allSettled([
      isPremiumExchange(exchange1Slug)
        ? getPremiumHistory({
            exchange: exchange1Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
      isPremiumExchange(exchange2Slug)
        ? getPremiumHistory({
            exchange: exchange2Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const [premium1, premium2] = value;
        const p1 = premium1.status === 'fulfilled' ? premium1.value : null;
        const p2 = premium2.status === 'fulfilled' ? premium2.value : null;
        setPremiumLines([
          {
            key: 'premium1',
            label: `${config.exchange1} premium`,
            color: EXCHANGE1_COLOR,
            data: p1 ? mapMsCandle(p1.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
          },
          {
            key: 'premium2',
            label: `${config.exchange2} premium`,
            color: EXCHANGE2_COLOR,
            data: p2 ? mapMsCandle(p2.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
          },
        ]);
        const failedLabels = [premium1.status === 'rejected' ? config.exchange1 : null, premium2.status === 'rejected' ? config.exchange2 : null].filter(
          (v): v is string => v != null,
        );
        setErrors((prev) => ({
          ...prev,
          premium: failedLabels.length >= 2 ? `Premium 查询失败：${failedLabels.join(' / ')}` : undefined,
        }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setPremiumLines([]);
        setErrors((prev) => ({ ...prev, premium: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    const markUnsupported = !isMarkExchange(exchange1Slug) || !isMarkExchange(exchange2Slug);
    Promise.all([
      isMarkExchange(exchange1Slug)
        ? getMarkPriceHistory({
            exchange: exchange1Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
      isMarkExchange(exchange2Slug)
        ? getMarkPriceHistory({
            exchange: exchange2Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const [mark1, mark2] = value;
        setMarkCandles([
          { key: 'mark1', label: `${config.exchange1} mark`, color: EXCHANGE1_COLOR, data: mark1 ? mapMsCandle(mark1.data) : [] },
          { key: 'mark2', label: `${config.exchange2} mark`, color: EXCHANGE2_COLOR, data: mark2 ? mapMsCandle(mark2.data) : [] },
        ]);
        setErrors((prev) => ({
          ...prev,
          mark: markUnsupported
            ? 'Mark Price 仅支持 Bybit / OKX / Binance / Gateio / Bitget；不支持的交易所会显示为空。'
            : undefined,
        }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setMarkCandles([]);
        setErrors((prev) => ({ ...prev, mark: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    const indexUnsupported = !isIndexExchange(exchange1Slug) || !isIndexExchange(exchange2Slug);
    Promise.all([
      isIndexExchange(exchange1Slug)
        ? getIndexPriceHistory({
            exchange: exchange1Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
      isIndexExchange(exchange2Slug)
        ? getIndexPriceHistory({
            exchange: exchange2Slug,
            symbol,
            interval: config.historyInterval,
            start_ts: ctx.startMs,
            end_ts: ctx.endMs,
          })
        : Promise.resolve(null),
    ])
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const [index1, index2] = value;
        setIndexCandles([
          { key: 'index1', label: `${config.exchange1} index`, color: EXCHANGE1_COLOR, data: index1 ? mapMsCandle(index1.data) : [] },
          { key: 'index2', label: `${config.exchange2} index`, color: EXCHANGE2_COLOR, data: index2 ? mapMsCandle(index2.data) : [] },
        ]);
        setErrors((prev) => ({
          ...prev,
          index: indexUnsupported
            ? 'Index Price 仅支持 Bybit / OKX / Binance / Gateio / Bitget；不支持的交易所会显示为空。'
            : undefined,
        }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setIndexCandles([]);
        setErrors((prev) => ({ ...prev, index: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);

    Promise.allSettled(OVERVIEW_DEPOSIT_EXCHANGES.map((exchange) => getDepositWithdrawStatus(exchange.value, symbol)))
      .then((value) => {
        if (querySeqRef.current !== querySeq) return;
        const mapped = value.map((item, idx) => {
          const exchange = OVERVIEW_DEPOSIT_EXCHANGES[idx];
          if (!exchange) {
            return { exchange: 'unknown', label: 'Unknown', rows: [], error: '未知交易所结果' } satisfies DepositResult;
          }
          if (item.status === 'fulfilled') {
            return {
              exchange: exchange.value,
              label: exchange.label,
              rows: item.value.data ?? [],
              error: null,
            } satisfies DepositResult;
          }
          return {
            exchange: exchange.value,
            label: exchange.label,
            rows: [],
            error: item.reason instanceof Error ? item.reason.message : String(item.reason),
          } satisfies DepositResult;
        });
        setDepositResults(mapped);
        setErrors((prev) => ({ ...prev, deposit: undefined }));
      })
      .catch((reason: unknown) => {
        if (querySeqRef.current !== querySeq) return;
        setDepositResults([]);
        setErrors((prev) => ({ ...prev, deposit: reason instanceof Error ? reason.message : String(reason) }));
      })
      .finally(finishTask);
  }, [
    buildQueryContext,
    config.exchange1,
    config.exchange2,
    config.historyInterval,
    config.interval,
    config.smaPeriod,
    exchange1Slug,
    exchange2Slug,
    instId1,
    instId2,
    symbol,
  ]);

  const refreshChart = useCallback(
    (key: OverviewRefreshKey) => {
      setPageError(null);
      const ctx = buildLatestEndQueryContext();
      if (!ctx) return;

      setRefreshingCharts((prev) => ({ ...prev, [key]: true }));

      const setRefreshError = (errorKey: keyof OverviewErrors, reason: unknown) => {
        setErrors((prev) => ({ ...prev, [errorKey]: reason instanceof Error ? reason.message : String(reason) }));
      };

      const finishRefresh = () => {
        setRefreshingCharts((prev) => ({ ...prev, [key]: false }));
      };

      if (key === 'price') {
        Promise.all([
          getCandles({
            exchange: config.exchange1,
            inst_id: instId1,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            interval: config.interval,
          }),
          getCandles({
            exchange: config.exchange2,
            inst_id: instId2,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            interval: config.interval,
          }),
        ])
          .then((value) => {
            setPriceCandles([
              { key: 'price1', label: `${config.exchange1}:${instId1}`, color: EXCHANGE1_COLOR, data: mapServerCandle(value[0].data) },
              { key: 'price2', label: `${config.exchange2}:${instId2}`, color: EXCHANGE2_COLOR, data: mapServerCandle(value[1].data) },
            ]);
            setErrors((prev) => ({ ...prev, price: undefined }));
          })
          .catch((reason: unknown) => {
            setPriceCandles([]);
            setRefreshError('price', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'forwardDetailed' || key === 'reverseDetailed') {
        getDetailedSpread({
          exchange1: config.exchange1,
          inst_id1: instId1,
          exchange2: config.exchange2,
          inst_id2: instId2,
          start_time: ctx.startTime,
          end_time: ctx.endTime,
          interval: config.interval,
          sma_period: config.smaPeriod,
        })
          .then((value) => {
            setDetailedSpreadRows(value.data);
            setErrors((prev) => ({
              ...prev,
              forwardDetailed: undefined,
              reverseDetailed: undefined,
            }));
          })
          .catch((reason: unknown) => {
            if (key === 'forwardDetailed') {
              setRefreshError('forwardDetailed', reason);
            } else {
              setRefreshError('reverseDetailed', reason);
            }
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'indexAdjusted') {
        (config.interval === 60 || config.interval === 300
          ? getIndexAdjustedSpread({
              exchange1: config.exchange1,
              inst_id1: instId1,
              exchange2: config.exchange2,
              inst_id2: instId2,
              start_time: ctx.startTime,
              end_time: ctx.endTime,
              interval: config.interval as 60 | 300,
              symbol1: symbol,
              symbol2: symbol,
              sma_period: config.smaPeriod,
            })
          : Promise.resolve(null)
        )
          .then((value) => {
            if (value == null) {
              setIndexAdjustedLines([]);
              setErrors((prev) => ({ ...prev, indexAdjusted: 'Index修正Spread仅支持 interval=60(1m) 或 300(5m)。' }));
              return;
            }
            setIndexAdjustedLines([
              {
                key: 'adjusted_spread',
                label: 'adjusted_spread',
                color: EXCHANGE1_COLOR,
                data: sortByTime(
                  value.data
                    .map((point): LineData<UTCTimestamp> | null => {
                      const time = toUtcTimestamp(point.timestamp);
                      if (time == null || !Number.isFinite(point.adjusted_spread)) return null;
                      return { time, value: point.adjusted_spread };
                    })
                    .filter((point): point is LineData<UTCTimestamp> => point != null),
                ),
              },
              {
                key: 'orderbook_spread',
                label: 'orderbook_spread',
                color: EXCHANGE2_COLOR,
                data: sortByTime(
                  value.data
                    .map((point): LineData<UTCTimestamp> | null => {
                      const time = toUtcTimestamp(point.timestamp);
                      if (time == null || !Number.isFinite(point.orderbook_spread)) return null;
                      return { time, value: point.orderbook_spread };
                    })
                    .filter((point): point is LineData<UTCTimestamp> => point != null),
                ),
              },
              {
                key: 'index_spread',
                label: 'index_spread',
                color: '#7c3aed',
                data: sortByTime(
                  value.data
                    .map((point): LineData<UTCTimestamp> | null => {
                      const time = toUtcTimestamp(point.timestamp);
                      if (time == null || !Number.isFinite(point.index_spread)) return null;
                      return { time, value: point.index_spread };
                    })
                    .filter((point): point is LineData<UTCTimestamp> => point != null),
                ),
              },
            ]);
            setErrors((prev) => ({ ...prev, indexAdjusted: undefined }));
          })
          .catch((reason: unknown) => {
            setIndexAdjustedLines([]);
            setRefreshError('indexAdjusted', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'funding') {
        Promise.all([
          getFundingRates({
            exchange: config.exchange1,
            inst_id: instId1,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            interval: config.interval,
          }),
          getFundingRates({
            exchange: config.exchange2,
            inst_id: instId2,
            start_time: ctx.startTime,
            end_time: ctx.endTime,
            interval: config.interval,
          }),
        ])
          .then((value) => {
            setFundingSettlementTimes({
              left: normalizeFundingTimeMs(value[0].data[value[0].data.length - 1]?.funding_time),
              right: normalizeFundingTimeMs(value[1].data[value[1].data.length - 1]?.funding_time),
            });
            setFundingLines([
              { key: 'funding1', label: `${config.exchange1}:${instId1}`, color: EXCHANGE1_COLOR, data: mapFundingLine(value[0].data) },
              { key: 'funding2', label: `${config.exchange2}:${instId2}`, color: EXCHANGE2_COLOR, data: mapFundingLine(value[1].data) },
            ]);
            setErrors((prev) => ({ ...prev, funding: undefined }));
          })
          .catch((reason: unknown) => {
            setFundingLines([]);
            setFundingSettlementTimes({ left: null, right: null });
            setRefreshError('funding', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'openInterest') {
        Promise.allSettled([
          isOpenInterestExchange(exchange1Slug)
            ? getOpenInterest({ exchange: config.exchange1, inst_id: instId1, start_time: ctx.startTime, end_time: ctx.endTime, limit: 10000 })
            : Promise.resolve(null),
          isOpenInterestExchange(exchange2Slug)
            ? getOpenInterest({ exchange: config.exchange2, inst_id: instId2, start_time: ctx.startTime, end_time: ctx.endTime, limit: 10000 })
            : Promise.resolve(null),
        ])
          .then((value) => {
            const [openInterest1, openInterest2] = value;
            const oi1 = openInterest1.status === 'fulfilled' ? openInterest1.value : null;
            const oi2 = openInterest2.status === 'fulfilled' ? openInterest2.value : null;
            setOpenInterestLines([
              {
                key: 'open_interest1',
                label: `${config.exchange1}:${instId1} OI USD`,
                color: EXCHANGE1_COLOR,
                data: oi1 ? mapOpenInterestUsdLine(oi1.data) : [],
              },
              {
                key: 'open_interest2',
                label: `${config.exchange2}:${instId2} OI USD`,
                color: EXCHANGE2_COLOR,
                data: oi2 ? mapOpenInterestUsdLine(oi2.data) : [],
              },
            ]);
            setErrors((prev) => ({ ...prev, openInterest: undefined }));
          })
          .catch((reason: unknown) => {
            setOpenInterestLines([]);
            setRefreshError('openInterest', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'tradingVolume') {
        Promise.allSettled([
          isTradingVolumeExchange(exchange1Slug)
            ? getTradingVolume({
                exchange: config.exchange1,
                market_type: 'future',
                symbol,
                inst_id: instId1,
                start_time: ctx.startTime,
                end_time: ctx.endTime,
                limit: 50000,
              })
            : Promise.resolve(null),
          isTradingVolumeExchange(exchange2Slug)
            ? getTradingVolume({
                exchange: config.exchange2,
                market_type: 'future',
                symbol,
                inst_id: instId2,
                start_time: ctx.startTime,
                end_time: ctx.endTime,
                limit: 50000,
              })
            : Promise.resolve(null),
        ])
          .then((value) => {
            const [volume1, volume2] = value;
            const v1 = volume1.status === 'fulfilled' ? volume1.value : null;
            const v2 = volume2.status === 'fulfilled' ? volume2.value : null;
            setTradingVolumeLines([
              {
                key: 'trading_volume1',
                label: `${config.exchange1}:${instId1} 24h quote volume`,
                color: EXCHANGE1_COLOR,
                data: v1 ? mapTradingVolumeQuoteLine(v1.data) : [],
              },
              {
                key: 'trading_volume2',
                label: `${config.exchange2}:${instId2} 24h quote volume`,
                color: EXCHANGE2_COLOR,
                data: v2 ? mapTradingVolumeQuoteLine(v2.data) : [],
              },
            ]);
            setErrors((prev) => ({ ...prev, tradingVolume: undefined }));
          })
          .catch((reason: unknown) => {
            setTradingVolumeLines([]);
            setRefreshError('tradingVolume', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'premium') {
        Promise.allSettled([
          isPremiumExchange(exchange1Slug)
            ? getPremiumHistory({ exchange: exchange1Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
            : Promise.resolve(null),
          isPremiumExchange(exchange2Slug)
            ? getPremiumHistory({ exchange: exchange2Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
            : Promise.resolve(null),
        ])
          .then((value) => {
            const [premium1, premium2] = value;
            const p1 = premium1.status === 'fulfilled' ? premium1.value : null;
            const p2 = premium2.status === 'fulfilled' ? premium2.value : null;
            setPremiumLines([
              {
                key: 'premium1',
                label: `${config.exchange1} premium`,
                color: EXCHANGE1_COLOR,
                data: p1 ? mapMsCandle(p1.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
              },
              {
                key: 'premium2',
                label: `${config.exchange2} premium`,
                color: EXCHANGE2_COLOR,
                data: p2 ? mapMsCandle(p2.data).map((point) => ({ time: point.time, value: point.close * 100 })) : [],
              },
            ]);
            setErrors((prev) => ({ ...prev, premium: undefined }));
          })
          .catch((reason: unknown) => {
            setPremiumLines([]);
            setRefreshError('premium', reason);
          })
          .finally(finishRefresh);
        return;
      }

      if (key === 'mark') {
        Promise.all([
          isMarkExchange(exchange1Slug)
            ? getMarkPriceHistory({ exchange: exchange1Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
            : Promise.resolve(null),
          isMarkExchange(exchange2Slug)
            ? getMarkPriceHistory({ exchange: exchange2Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
            : Promise.resolve(null),
        ])
          .then((value) => {
            setMarkCandles([
              { key: 'mark1', label: `${config.exchange1} mark`, color: EXCHANGE1_COLOR, data: value[0] ? mapMsCandle(value[0].data) : [] },
              { key: 'mark2', label: `${config.exchange2} mark`, color: EXCHANGE2_COLOR, data: value[1] ? mapMsCandle(value[1].data) : [] },
            ]);
            setErrors((prev) => ({ ...prev, mark: undefined }));
          })
          .catch((reason: unknown) => {
            setMarkCandles([]);
            setRefreshError('mark', reason);
          })
          .finally(finishRefresh);
        return;
      }

      Promise.all([
        isIndexExchange(exchange1Slug)
          ? getIndexPriceHistory({ exchange: exchange1Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
          : Promise.resolve(null),
        isIndexExchange(exchange2Slug)
          ? getIndexPriceHistory({ exchange: exchange2Slug, symbol, interval: config.historyInterval, start_ts: ctx.startMs, end_ts: ctx.endMs })
          : Promise.resolve(null),
      ])
        .then((value) => {
          setIndexCandles([
            { key: 'index1', label: `${config.exchange1} index`, color: EXCHANGE1_COLOR, data: value[0] ? mapMsCandle(value[0].data) : [] },
            { key: 'index2', label: `${config.exchange2} index`, color: EXCHANGE2_COLOR, data: value[1] ? mapMsCandle(value[1].data) : [] },
          ]);
          setErrors((prev) => ({ ...prev, index: undefined }));
        })
        .catch((reason: unknown) => {
          setIndexCandles([]);
          setRefreshError('index', reason);
        })
        .finally(finishRefresh);
    },
    [
      buildLatestEndQueryContext,
      config.exchange1,
      config.exchange2,
      config.historyInterval,
      config.interval,
      config.smaPeriod,
      exchange1Slug,
      exchange2Slug,
      instId1,
      instId2,
      symbol,
    ],
  );

  useEffect(() => {
    if (!pendingAutoQuery || isLoading) return;
    setPendingAutoQuery(false);
    void runQuery();
  }, [isLoading, pendingAutoQuery, runQuery]);

  const renderCandleChart = (lines: CandleLine[]) =>
    lines.some((line) => line.data.length > 0) ? (
      <>
        <div className="overview-legend">
          {lines.map((line) => (
            <span key={line.key}>
              <i className="overview-dot" style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>
        <TradingViewPairCandleChart series={lines} syncGroupRef={chartSyncGroupRef} />
      </>
    ) : (
      <div className="overview-empty">暂无数据，请调整参数后查询。</div>
    );

  const renderLineChart = (lines: ValueLine[], valueSuffix = '%', valuePrecision = 6) =>
    lines.some((line) => line.data.length > 0) ? (
      <>
        <div className="overview-legend">
          {lines.map((line) => (
            <span key={line.key}>
              <i className="overview-dot" style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>
        <TradingViewLineChart lines={lines} syncGroupRef={chartSyncGroupRef} valueSuffix={valueSuffix} valuePrecision={valuePrecision} />
      </>
    ) : (
      <div className="overview-empty">暂无数据，请调整参数后查询。</div>
    );

  const renderDetailedSpreadChart = (
    lines: ValueLine[],
    viewMode: DetailedSpreadViewMode,
    onToggleViewMode: () => void,
  ) =>
    lines.some((line) => line.data.length > 0) ? (
      <>
        <div className="overview-legend">
          {lines.map((line) => (
            <span key={line.key}>
              <i className="overview-dot" style={{ background: line.color }} />
              {line.label}
            </span>
          ))}
        </div>
        <div className="overview-chart-shell">
          <button type="button" className="overview-chart-mode-toggle" onClick={onToggleViewMode}>
            {detailedSpreadViewModeLabel(viewMode)}
          </button>
          <TradingViewLineChart lines={lines} syncGroupRef={chartSyncGroupRef} valueSuffix="%" valuePrecision={4} />
        </div>
      </>
    ) : (
      <div className="overview-empty">暂无数据，请调整参数后查询。</div>
    );

  return (
    <div className={`overview-page${embedded ? ' is-embedded' : ''}`}>
      {embedded ? null : (
        <div className="overview-header">
          <h1>Overview</h1>
          <p>默认一次性查询并展示全部图表内容。</p>
        </div>
      )}

      <form className="overview-config" onSubmit={runQuery}>
        <div className="overview-config-grid">
          <label>
            <span>Symbol</span>
            <input value={config.symbol} onChange={setField('symbol')} placeholder="APE" required />
          </label>
          <label>
            <span>交易所 1</span>
            <select value={config.exchange1} onChange={setField('exchange1')}>
              <option value={config.exchange1}>{config.exchange1}</option>
              {exchanges
                .filter((exchange) => exchange !== config.exchange1)
                .map((exchange) => (
                  <option key={exchange} value={exchange}>
                    {exchange}
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>交易所 2</span>
            <select value={config.exchange2} onChange={setField('exchange2')}>
              <option value={config.exchange2}>{config.exchange2}</option>
              {exchanges
                .filter((exchange) => exchange !== config.exchange2)
                .map((exchange) => (
                  <option key={exchange} value={exchange}>
                    {exchange}
                  </option>
                ))}
            </select>
          </label>
          <label>
            <span>开始时间 UTC</span>
            <input type="datetime-local" value={config.startTime} onChange={setField('startTime')} required />
          </label>
          <label>
            <span>结束时间 UTC</span>
            <input type="datetime-local" value={config.endTime} onChange={setField('endTime')} required />
          </label>
          <label>
            <span>K线 interval(秒)</span>
            <input type="number" min="1" value={config.interval} onChange={setField('interval')} required />
          </label>
          <label>
            <span>历史 interval</span>
            <select value={config.historyInterval} onChange={setField('historyInterval')}>
              {['1m', '5m', '15m', '30m', '1h', '4h', '1d'].map((interval) => (
                <option key={interval} value={interval}>
                  {interval}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>SMA 周期</span>
            <input type="number" min="1" value={config.smaPeriod} onChange={setField('smaPeriod')} required />
          </label>
        </div>
        <div className="overview-actions">
          <button type="submit" disabled={isLoading}>
            {isLoading ? '查询中...' : '查询全部图表'}
          </button>
          <span>
            inst_id：{config.exchange1}={instId1 || '-'}，{config.exchange2}={instId2 || '-'}
          </span>
        </div>
      </form>

      {pageError && <div className="overview-error">{pageError}</div>}

      <section className="overview-card">
        <div className="overview-card-title">
          <h2>交易所基础信息</h2>
          <span>{config.exchange1}:{instId1 || '-'} / {config.exchange2}:{instId2 || '-'}</span>
        </div>
        {instrumentInfoError ? (
          <div className="overview-card-error">{instrumentInfoError}</div>
        ) : instrumentInfoLoading ? (
          <div className="overview-empty">基础信息查询中...</div>
        ) : (
          <div className="overview-instrument-grid">
            {[
              { exchange: config.exchange1, info: instrumentInfo1, instId: instId1 },
              { exchange: config.exchange2, info: instrumentInfo2, instId: instId2 },
            ].map((item) => (
              <section key={`${item.exchange}-${item.instId}`} className="overview-instrument-card">
                <h3>{item.exchange}</h3>
                {item.info == null ? (
                  <div className="overview-empty">未查到 {item.instId || '-'} 的合约信息</div>
                ) : (
                  <div className="overview-instrument-fields">
                    <div><span>symbol</span><strong>{item.info.symbol}</strong></div>
                    <div><span>inst_id</span><strong>{item.info.inst_id}</strong></div>
                    <div><span>status</span><strong>{item.info.status}</strong></div>
                    <div><span>base / quote</span><strong>{item.info.base_asset} / {item.info.quote_asset}</strong></div>
                    <div><span>lot_sz</span><strong>{formatPrice(item.info.lot_sz)}</strong></div>
                    <div><span>min_lot_sz</span><strong>{formatPrice(item.info.min_lot_sz)}</strong></div>
                    <div><span>min_usd_sz</span><strong>{formatPrice(item.info.min_usd_sz)}</strong></div>
                    <div><span>ct_val</span><strong>{formatPrice(item.info.ct_val)}</strong></div>
                    <div><span>max_mkt_sz</span><strong>{formatPrice(item.info.max_mkt_sz)}</strong></div>
                    <div><span>funding_interval</span><strong>{item.info.funding_interval} min</strong></div>
                    <div><span>upper / lower funding</span><strong>{item.info.upper_funding_rate} / {item.info.lower_funding_rate}</strong></div>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}
      </section>

      <div className="overview-summary">
        <span>价格 K线：{priceCandles.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Long1 Short2 Detailed：{forwardDetailedLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Short1 Long2 Detailed：{reverseDetailedLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Mark Price：{markCandles.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Index Price：{indexCandles.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Premium：{premiumLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Funding：{fundingLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Open Interest：{openInterestLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Trading Volume：{tradingVolumeLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
        <span>Index修正Spread：{indexAdjustedLines.reduce((sum, line) => sum + line.data.length, 0)}</span>
      </div>

      <div className="overview-grid">
        <ChartCard
          title="价格 K 线"
          subtitle={`${config.exchange1}:${instId1} / ${config.exchange2}:${instId2}`}
          error={errors.price}
          onRefresh={() => refreshChart('price')}
          isRefreshing={Boolean(refreshingCharts.price)}
          refreshDisabled={isLoading}
        >
          {renderCandleChart(priceCandles)}
        </ChartCard>

        <ChartCard
          title="Long1 Short2 Detailed Spread"
          subtitle={`${config.exchange1}:${instId1} -> ${config.exchange2}:${instId2}`}
          error={errors.forwardDetailed}
          onRefresh={() => refreshChart('forwardDetailed')}
          isRefreshing={Boolean(refreshingCharts.forwardDetailed)}
          refreshDisabled={isLoading}
        >
          {renderDetailedSpreadChart(forwardDetailedLines, forwardSpreadViewMode, () =>
            setForwardSpreadViewMode((mode) => (mode === 'orderbook' ? 'mid' : 'orderbook')),
          )}
        </ChartCard>

        <ChartCard
          title="Short1 Long2 Detailed Spread"
          subtitle={`${config.exchange2}:${instId2} -> ${config.exchange1}:${instId1}`}
          error={errors.reverseDetailed}
          onRefresh={() => refreshChart('reverseDetailed')}
          isRefreshing={Boolean(refreshingCharts.reverseDetailed)}
          refreshDisabled={isLoading}
        >
          {renderDetailedSpreadChart(reverseDetailedLines, reverseSpreadViewMode, () =>
            setReverseSpreadViewMode((mode) => (mode === 'orderbook' ? 'mid' : 'orderbook')),
          )}
        </ChartCard>

        <ChartCard
          title="Index修正Spread"
          subtitle={`${config.exchange1}:${instId1} - ${config.exchange2}:${instId2}`}
          error={errors.indexAdjusted}
          onRefresh={() => refreshChart('indexAdjusted')}
          isRefreshing={Boolean(refreshingCharts.indexAdjusted)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(indexAdjustedLines, '%', 4)}
        </ChartCard>

        <ChartCard
          title="历史资金费率"
          subtitle={fundingSubtitle}
          error={errors.funding}
          onRefresh={() => refreshChart('funding')}
          isRefreshing={Boolean(refreshingCharts.funding)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(fundingChartLines, '%', 4)}
        </ChartCard>

        <ChartCard
          title="Open Interest"
          subtitle={`${config.exchange1}:${instId1} / ${config.exchange2}:${instId2}`}
          error={errors.openInterest}
          onRefresh={() => refreshChart('openInterest')}
          isRefreshing={Boolean(refreshingCharts.openInterest)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(openInterestLines, ' USD')}
        </ChartCard>

        <ChartCard
          title="Trading Volume 24h"
          subtitle={`${config.exchange1}:${instId1} / ${config.exchange2}:${instId2} | future`}
          error={errors.tradingVolume}
          onRefresh={() => refreshChart('tradingVolume')}
          isRefreshing={Boolean(refreshingCharts.tradingVolume)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(tradingVolumeLines, ' quote')}
        </ChartCard>

        <ChartCard
          title="Premium 历史"
          subtitle={`${config.exchange1} / ${config.exchange2}`}
          error={errors.premium}
          onRefresh={() => refreshChart('premium')}
          isRefreshing={Boolean(refreshingCharts.premium)}
          refreshDisabled={isLoading}
        >
          {renderLineChart(premiumLines)}
        </ChartCard>

        <ChartCard
          title="Mark Price K 线"
          subtitle={`${config.exchange1} / ${config.exchange2}`}
          error={errors.mark}
          onRefresh={() => refreshChart('mark')}
          isRefreshing={Boolean(refreshingCharts.mark)}
          refreshDisabled={isLoading}
        >
          {renderCandleChart(markCandles)}
        </ChartCard>

        <ChartCard
          title="Index Price K 线"
          subtitle={`${config.exchange1} / ${config.exchange2}`}
          error={errors.index}
          onRefresh={() => refreshChart('index')}
          isRefreshing={Boolean(refreshingCharts.index)}
          refreshDisabled={isLoading}
        >
          {renderCandleChart(indexCandles)}
        </ChartCard>

        <ChartCard title="充提状态" subtitle={symbol || '-'} error={errors.deposit}>
          {depositResults.length === 0 ? (
            <div className="overview-empty">暂无充提状态数据。</div>
          ) : (
            <div className="overview-deposit-grid">
              {depositResults.map((result) => (
                <section key={result.exchange} className="overview-deposit-card">
                  <h3>{result.label}</h3>
                  {result.error ? (
                    <div className="overview-card-error">{result.error}</div>
                  ) : result.rows.length === 0 ? (
                    <div className="overview-empty">无数据</div>
                  ) : (
                    <table className="overview-deposit-table">
                      <thead>
                        <tr>
                          <th>链</th>
                          <th>充值</th>
                          <th>提现</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.rows.map((row) => (
                          <tr key={`${result.exchange}-${row.coin}-${row.chain}`}>
                            <td>{row.chain}</td>
                            <td className={row.deposit_enabled ? 'overview-status-ok' : 'overview-status-no'}>
                              {row.deposit_enabled ? '开放' : '关闭'}
                            </td>
                            <td className={row.withdraw_enabled ? 'overview-status-ok' : 'overview-status-no'}>
                              {row.withdraw_enabled ? '开放' : '关闭'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
